// The studio session: one immutable draft in undo/redo history, everything else
// derived. Shared by the full-page studio and the planogram side-panel overlay.
import { useMemo, useState } from "react";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import {
  POG_PREFERRED_ORDER,
  pogToStudioItems,
  type PogAdapterOptions,
} from "../adapters/pogToStudioItems";
import { buildAsBuiltLayout, buildLayout } from "../domain/buildLayout";
import { auditContiguity } from "../domain/contiguityAudit";
import {
  draftFromInference,
  inferSpine,
  type AttributeCandidate,
  type InferenceOptions,
} from "../domain/spineInference";
import {
  canRedo,
  canUndo,
  createStudioHistory,
  recordSnapshot,
  redo,
  undo,
  type StudioHistory,
} from "../domain/studioHistory";
import { getStudioHistory, setStudioHistory, studioHistoryKey } from "./studioHistoryStore";
import { isFlagSettled } from "../domain/studioTransactions";
import {
  Flag,
  FlagSeverity,
  PATH_SEPARATOR,
  StudioDraft,
  effectiveAttrs,
} from "../domain/studioTypes";
import { detectExceptions } from "../resolver/detectExceptions";
import { projectTree } from "../projections/treeProjection";

export const blockingFlagsOnly = (flags: readonly Flag[]): Flag[] =>
  flags.filter((flag) => flag.severity === "blocking");

export interface StudioSessionOptions {
  adapterOptions?: PogAdapterOptions;
  inferenceOptions?: InferenceOptions;
  /** @deprecated Prefer inferenceOptions.preferredOrder. */
  preferredOrder?: readonly string[];
  /** Base pog id, so history survives unmount. Omitted off-route: no persistence. */
  basePogId?: number | null;
  /** Stable attributes that define provenance blocks, major to minor. */
  blockIdentityAttributes?: readonly string[];
  /** Attribute whose values use quantity-aware resolver ordering. */
  quantityAttribute?: string;
  /** Attribute treated as the terminal variant by exception taxonomy. */
  terminalVariantAttribute?: string;
  /** Names levels the configuration declares but the load does not carry. Never an
   *  inference input. */
  declaredValues?: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
  declaredLabels?: Readonly<Record<string, string>>;
}

/** Does this draft still describe the attribute table the data now offers? */
export const draftSpeaksAttributeTable = (
  draft: Pick<StudioDraft, "spine">,
  candidates: readonly AttributeCandidate[],
): boolean => {
  const live = new Set(candidates.map((candidate) => candidate.attr));
  return draft.spine.every((level) => live.has(level.attr));
};

export const useStudioSession = (pogs: readonly Pog[], options: StudioSessionOptions = {}) => {
  const {
    adapterOptions,
    inferenceOptions,
    preferredOrder,
    blockIdentityAttributes,
    quantityAttribute,
    terminalVariantAttribute,
    declaredValues,
    declaredLabels,
    basePogId,
  } = options;
  const historyKey = studioHistoryKey(basePogId);

  const { items, runs, candidates } = useMemo(
    () => pogToStudioItems(pogs, adapterOptions),
    [pogs, adapterOptions],
  );

  const initialDraft = useMemo(
    () =>
      draftFromInference(
        inferSpine(items, candidates, runs, {
          ...inferenceOptions,
          preferredOrder: preferredOrder ?? inferenceOptions?.preferredOrder ?? POG_PREFERRED_ORDER,
          // Raw size sequences; it must not take a blocking rung ahead of a level like sub-brand.
          quantityAttribute: inferenceOptions?.quantityAttribute ?? quantityAttribute,
        }),
      ),
    [items, candidates, runs, preferredOrder, inferenceOptions, quantityAttribute],
  );
  // Seeded from the module-level store and written through to it, so the session
  // survives unmount — but only a draft that still speaks this attribute table;
  // a stale-vocabulary draft is discarded for a fresh inference.
  const [history, setHistoryState] = useState(() => {
    const stored = getStudioHistory(historyKey);
    return stored && draftSpeaksAttributeTable(stored.present, candidates)
      ? stored
      : createStudioHistory(initialDraft);
  });
  const setHistory = (update: (current: StudioHistory) => StudioHistory) => {
    setHistoryState((current) => {
      const next = update(current);
      setStudioHistory(historyKey, next);
      return next;
    });
  };
  const draft = history.present;

  const baseLayout = useMemo(() => buildAsBuiltLayout(items), [items]);
  const layout = useMemo(() => buildLayout(items, draft, runs), [items, draft, runs]);

  // Detected on the as-built plan with the resolver's own audit measures, so an
  // exception is raised exactly when studio.check() would call it unresolved.
  const detected = useMemo(
    () =>
      detectExceptions(items, runs, draft, {
        quantityAttribute,
        crossLevelNameFlags: false,
        blockIdentityAttributes,
        verticalBlockShare: inferenceOptions?.verticalBlockShare,
        terminalVariantAttribute,
        declaredValues,
        declaredLabels,
      }),
    [
      items,
      runs,
      draft.spine,
      draft.valueOrder,
      draft.attrOverrides,
      /* A branch-local level move touches only these; omitting them staled the census. */
      draft.scopedLevelOrder,
      draft.scopedValueOrder,
      draft.levelBands,
      quantityAttribute,
      blockIdentityAttributes,
      inferenceOptions?.verticalBlockShare,
      terminalVariantAttribute,
      declaredValues,
      declaredLabels,
    ],
  );
  // The studio shows blocking findings only; "to check" and "to review" rows are advisory
  // wording about the tree and were read as violations. They stay in the detector for tests.
  const flags = blockingFlagsOnly(detected.flags);

  const blockOf = useMemo(() => {
    const attrs =
      blockIdentityAttributes?.filter((attr) => draft.spine.some((level) => level.attr === attr)) ??
      draft.spine.slice(0, 2).map((level) => level.attr);
    return (item: (typeof items)[number]) => {
      const values = effectiveAttrs(item, draft);
      return attrs.map((attr) => values[attr] ?? "").join(PATH_SEPARATOR);
    };
  }, [draft, blockIdentityAttributes]);

  const provenance = useMemo(
    () => auditContiguity(baseLayout, layout, items, blockOf),
    [baseLayout, layout, items, blockOf],
  );

  const tree = useMemo(() => projectTree(items, draft, flags), [items, draft, flags]);

  const settledFlagIds = useMemo(
    () => new Set(flags.filter((flag) => isFlagSettled(draft, flag.id)).map((flag) => flag.id)),
    [flags, draft],
  );
  const openFlags = useMemo(
    () => flags.filter((flag) => !settledFlagIds.has(flag.id)),
    [flags, settledFlagIds],
  );
  const severityCounts = useMemo(() => {
    const counts: Record<FlagSeverity, number> = { blocking: 0, toCheck: 0, toReview: 0 };
    for (const flag of openFlags) counts[flag.severity] += 1;
    return counts;
  }, [openFlags]);

  const commit = (next: StudioDraft) => {
    if (next !== draft) setHistory((current) => recordSnapshot(current, next));
  };

  /** SKUs under a tree node (scope path key), for highlighting. */
  const skuIdsUnderNode = (nodeId: string): string[] => {
    const values = nodeId.split(PATH_SEPARATOR);
    return items
      .filter((item) => {
        const attrs = effectiveAttrs(item, draft);
        let valueIndex = 0;
        for (const level of draft.spine) {
          if (valueIndex >= values.length) break;
          if ((attrs[level.attr] ?? "") === values[valueIndex]) valueIndex += 1;
        }
        return valueIndex >= values.length;
      })
      .map((item) => item.skuId);
  };

  return {
    items,
    runs,
    candidates,
    draft,
    commit,
    undo: () => setHistory((current) => undo(current)),
    redo: () => setHistory((current) => redo(current)),
    canUndo: canUndo(history),
    canRedo: canRedo(history),
    /** Committed edits between here and the untouched plan. At 1 the next undo lands
     *  on the base, and the caller must restore the AS-BUILT view, not rebuild it. */
    undoDepth: history.past.length,
    baseLayout,
    layout,
    flags,
    resolverInputs: detected.inputs,
    openFlags,
    settledFlagIds,
    severityCounts,
    provenance,
    tree,
    skuIdsUnderNode,
  };
};

export type StudioSession = ReturnType<typeof useStudioSession>;

/** All flags (settled included) sorted blocking-first for list views. */
export const sortFlagsBySeverity = (flags: readonly Flag[]): Flag[] => {
  const rank: Record<FlagSeverity, number> = { blocking: 0, toCheck: 1, toReview: 2 };
  return [...flags].sort((a, b) => rank[a.severity] - rank[b.severity]);
};
